/**
 * Le verdict : est-ce que je peux ouvrir ma fenêtre ?
 *
 * Hiérarchie des preuves, dans cet ordre strict :
 *
 *   1. Le nez de l'utilisateur. Il sent des choses qu'aucun capteur ne mesure.
 *   2. Mesuré : capteurs citoyens au sol dans un rayon de 25 km.
 *   3. Estimé : modèle européen CAMS, quand aucun capteur n'est disponible.
 *   4. Déduit : le vent. Il ne peut JAMAIS, à lui seul, justifier d'ouvrir.
 *      Il ne fait que dégrader un verdict, jamais l'améliorer. Cette règle
 *      est appliquée à l'affichage, pas ici.
 */
import {
  LOCAL_MEASUREMENT_MIN_SENSORS,
  MODEL_MAX_AGE_MIN,
  NOSE_MAX_AGE_MIN,
  SENSOR_MAX_AGE_MIN,
  SENSOR_TRUST_KM,
} from './constants';
import { ageInMinutes, formatClock } from './format';
import { median } from './geo';
import type {
  AirSnapshot,
  NoseEntry,
  SensorReading,
  Verdict,
  VerdictBand,
  VerdictKey,
} from './types';

/**
 * Seuils de PM2,5 en µg/m³, pour une décision IMMÉDIATE : aérer ou non.
 *
 * Ce ne sont pas les moyennes réglementaires sur 24 heures. L'indice européen
 * EAQI n'est volontairement pas utilisé : c'est une moyenne glissante sur
 * 24 heures, qui traîne les pics passés. Pendant un incendie il peut indiquer
 * "extrêmement mauvais" alors que l'air est redevenu propre depuis deux
 * heures, ce qui est exactement l'inverse du service à rendre.
 *
 * Les titres DÉCRIVENT l'air, ils ne donnent pas d'ordre. "Ouvre la fenêtre"
 * et "Ferme tout" étaient des impératifs. Pour un outil non officiel consulté
 * par des personnes âgées et des asthmatiques, c'est à la fois présomptueux et
 * imprudent : l'application constate un état de l'air, elle ne décide pas à la
 * place de qui que ce soit. Le conseil est relégué dans `advice`, sous forme
 * de suggestion.
 */
export const VERDICT_BANDS: readonly VerdictBand[] = [
  {
    key: 'open',
    max: 12,
    title: "L'air est propre",
    colorVar: '--ok',
    advice: 'Tu peux aérer.',
  },
  {
    key: 'short',
    max: 25,
    title: 'Air correct',
    colorVar: '--ok',
    advice: "Aère par à-coups plutôt qu'en continu.",
  },
  {
    key: 'no',
    max: 50,
    title: 'Air chargé',
    colorVar: '--mid',
    advice: "Plus de particules que d'habitude. Mieux vaut garder fermé.",
  },
  {
    key: 'close',
    max: Infinity,
    title: 'Air très chargé',
    colorVar: '--bad',
    advice: 'Mieux vaut garder fermé et éviter de sortir.',
  },
];

const WORST_BAND = VERDICT_BANDS[VERDICT_BANDS.length - 1]!;

/**
 * Quand aucune donnée ne permet de trancher.
 *
 * Le conseil disait "Dans le doute, garde fermé". C'était encore une
 * recommandation appuyée sur rien. Sans mesure, la seule chose honnête est de
 * dire qu'on ne sait pas.
 */
export const UNKNOWN_BAND: VerdictBand = {
  key: 'unknown',
  max: Infinity,
  title: "Pas d'info ici",
  colorVar: '--faint',
  advice: 'Aucune mesure ici. Impossible de savoir.',
};

export function bandForPm25(pm25: number): VerdictBand {
  return VERDICT_BANDS.find((band) => pm25 < band.max) ?? WORST_BAND;
}

export function bandByKey(key: VerdictKey): VerdictBand {
  if (key === 'unknown') return UNKNOWN_BAND;
  return VERDICT_BANDS.find((band) => band.key === key) ?? UNKNOWN_BAND;
}

/**
 * Rang de sévérité d'une bande.
 *
 * `unknown` vaut autant que "chargé" : ne pas savoir n'est pas une bonne
 * nouvelle, et l'application ne parie pas sur l'absence de danger.
 */
export function bandSeverity(key: VerdictKey): number {
  if (key === 'unknown') return 2;
  const index = VERDICT_BANDS.findIndex((band) => band.key === key);
  return index < 0 ? 0 : index;
}

const severity = bandSeverity;

/**
 * La plus sévère de deux bandes.
 *
 * Utilisée pour appliquer un plancher de prudence sans jamais adoucir un
 * verdict déjà mauvais.
 */
export function worstBand(a: VerdictBand, b: VerdictBand): VerdictBand {
  return severity(b.key) > severity(a.key) ? b : a;
}

/**
 * Dégrade un verdict de `steps` crans, sans jamais dépasser le dernier.
 * Il n'existe volontairement pas de fonction inverse.
 */
export function degrade(band: VerdictBand, steps: number): VerdictBand {
  if (steps <= 0) return band;
  const index = VERDICT_BANDS.findIndex((candidate) => candidate.key === band.key);
  if (index < 0) return band;
  return VERDICT_BANDS[Math.min(VERDICT_BANDS.length - 1, index + steps)]!;
}

/** Les deux verdicts assez clairs pour qu'un feu au vent doive les nuancer. */
export function isPermissive(key: VerdictKey): boolean {
  return key === 'open' || key === 'short';
}

export interface VerdictInput {
  sensors: readonly SensorReading[];
  air: AirSnapshot | null;
  nose: readonly NoseEntry[];
  now: number;
}

/** Les capteurs encore assez récents pour décider. */
export function freshSensors(
  sensors: readonly SensorReading[],
  now: number,
): readonly SensorReading[] {
  return sensors.filter((sensor) => ageInMinutes(sensor.at, now) < SENSOR_MAX_AGE_MIN);
}

/**
 * Les capteurs à la fois récents et assez proches pour être représentatifs.
 *
 * Le rayon de collecte est de 25 km parce qu'il faut bien trouver des capteurs
 * quelque part. Mais à 25 km, un capteur mesure un autre air que le vôtre.
 */
export function localSensors(
  sensors: readonly SensorReading[],
  now: number,
): readonly SensorReading[] {
  return freshSensors(sensors, now).filter(
    (sensor) => sensor.distanceKm <= SENSOR_TRUST_KM,
  );
}

/** Une mesure locale digne de ce nom : assez de capteurs, assez près. */
export function hasLocalMeasurement(
  sensors: readonly SensorReading[],
  now: number,
): boolean {
  return localSensors(sensors, now).length >= LOCAL_MEASUREMENT_MIN_SENSORS;
}

/** Le modèle est-il exploitable, ou trop vieux ? */
export function isModelUsable(air: AirSnapshot | null, now: number): boolean {
  return (
    air !== null &&
    air.pm25 !== null &&
    air.at !== null &&
    Number.isFinite(air.at) &&
    ageInMinutes(air.at, now) < MODEL_MAX_AGE_MIN
  );
}

export function decide(input: VerdictInput): Verdict {
  const { air, nose, now } = input;

  // 1. Le nez passe devant tout le reste.
  const smell = nose.find(
    (entry) => entry.smoke && ageInMinutes(entry.at, now) < NOSE_MAX_AGE_MIN,
  );
  if (smell) {
    return {
      band: WORST_BAND,
      source: null,
      reason:
        `Tu as signalé de la fumée à ${formatClock(smell.at, now)}. ` +
        "Ton nez sent des choses qu'aucun capteur ne mesure.",
      basis: 'nose',
      pm25: null,
      measured: true,
      local: true,
      nearestSensorKm: null,
    };
  }

  const fresh = freshSensors(input.sensors, now);
  const near = fresh.filter((sensor) => sensor.distanceKm <= SENSOR_TRUST_KM);
  const modelUsable = isModelUsable(air, now);

  /*
   * Les capteurs proches priment sur les lointains, et ne se mélangent pas à
   * eux. Noyer deux capteurs situés à 3 km dans une médiane calculée avec cinq
   * capteurs à 24 km reviendrait à diluer la seule information pertinente.
   */
  const used = near.length > 0 ? near : fresh;
  const nearestSensorKm =
    used.length > 0 ? Math.min(...used.map((sensor) => sensor.distanceKm)) : null;

  let pm25: number;
  let source: string;
  let thinEvidence: boolean;
  let isLocal: boolean;

  if (used.length > 0) {
    // 2. Mesuré. La médiane, et non la moyenne : voir geo.median.
    pm25 = median(used.map((sensor) => sensor.pm25)) ?? 0;
    const latest = Math.max(...used.map((sensor) => sensor.at));
    source =
      near.length > 0
        ? `mesuré à moins de ${SENSOR_TRUST_KM} km, à ${formatClock(latest, now)}`
        : `mesuré à ${Math.round(nearestSensorKm ?? 0)} km d'ici, à ${formatClock(latest, now)}`;
    thinEvidence = near.length < LOCAL_MEASUREMENT_MIN_SENSORS;
    isLocal = near.length >= LOCAL_MEASUREMENT_MIN_SENSORS;
  } else if (modelUsable && air && air.pm25 !== null) {
    // 3. Estimé.
    pm25 = air.pm25;
    source = `estimation de ${formatClock(air.at, now)}`;
    thinEvidence = true;
    isLocal = false;
  } else {
    // 4. Rien d'exploitable.
    return {
      band: UNKNOWN_BAND,
      source: null,
      basis: 'none',
      reason: UNKNOWN_BAND.advice,
      pm25: null,
      measured: false,
      local: false,
      nearestSensorKm: null,
    };
  }

  let band = bandForPm25(pm25);
  let steps = 0;

  // Une preuve mince ne doit pas produire un feu vert franc.
  if (thinEvidence && isPermissive(band.key)) steps = 1;

  // Le modèle voit bien pire que les capteurs : ils sont peut-être mal placés.
  if (used.length > 0 && modelUsable && air && air.pm25 !== null) {
    if (air.pm25 > Math.max(25, pm25 * 3)) steps = Math.max(steps, 1);
  }

  // Ça monte franchement à l'heure suivante : autant le dire tout de suite.
  if (air && air.nextPm25 !== null) {
    if (air.nextPm25 > 25 && air.nextPm25 > pm25 * 1.8) steps = Math.max(steps, 1);
  }

  band = degrade(band, steps);

  return {
    band,
    basis: used.length > 0 ? 'sensors' : 'model',
    source,
    reason: band.advice,
    pm25,
    measured: used.length > 0,
    local: isLocal,
    nearestSensorKm,
  };
}
